import { useCallback, useContext, useEffect, useRef, useState } from "react"
import { get, post, patch } from "helpers/api_helper"
import { store as socketStore } from "components/Common/Socket/store"
import { newPlayerOperationId } from "pages/UserAccounts/PlayerAccounts/playerInvitationState"
import { competitionOperationMessage, runCompetitionOperation } from "./competitionOperation"

// The editor saves against a revision of the whole event, which any change to the event moves (a registration in another
// competition too). The draft keeps that revision, so saving it again cannot succeed until the editor knows what changed: the
// editor replaces these words once it has read the competition again. Every other code keeps the competition's own words, the
// phase-lock text included.
export const editorOperationMessage = code => code === "COMPETITION_REVISION_CONFLICT"
  ? "The event changed after this form was opened (for example, a new registration in one of its competitions), so nothing was saved. "
    + "Reload the current details before saving; reloading discards your draft."
  : competitionOperationMessage(code)

export default function useCompetitionEditorOperation({ eventId, competitionId, userId, onConfirmed }) {
  const { state: { socket: { socket, socketClientId } = {} } = {} } = useContext(socketStore)
  const key = `competition-editor:${userId}:${eventId}:${competitionId || "new"}`
  const action = competitionId ? "edit" : "create"
  const active = useRef(null), confirmed = useRef(onConfirmed)
  const [state, setState] = useState({})
  confirmed.current = onConfirmed
  const observe = useCallback((operation, submit, firstSubmission = false) => {
    active.current?.runner.cancel()
    const runner = runCompetitionOperation({ operation, socket, submit, firstSubmission, messageForCode: editorOperationMessage,
      read: operationId => get("/admin/competition-operations/" + operationId) })
    const request = { operation, runner }
    active.current = request
    setState({ status: "pending", operation })
    const completion = runner.promise.then(result => {
      if (active.current !== request || result.status === "cancelled") return
      setState({ ...result, operation })
      if (["saved", "error"].includes(result.status)) {
        try { sessionStorage.removeItem(key) } catch { /* A retained ID can only recover the same result. */ }
        active.current = null
        if (result.status === "saved") confirmed.current?.(result)
      }
      return result
    })
    return completion
  }, [key, socket])
  useEffect(() => {
    setState({})
    if (userId) {
      try {
        const saved = JSON.parse(sessionStorage.getItem(key) || "null")
        if (saved?.eventId === eventId && saved.action === action && saved.competitionId === competitionId && saved.operationId && saved.correlationId) observe(saved)
      } catch { /* A malformed record must never submit a mutation. */ }
    }
    return () => { active.current?.runner.cancel(); active.current = null }
  }, [eventId, competitionId, action, key, observe, userId])
  // `options`: the save's top-level flags beside the details (a Tenis10 fee's `applyClubCollectedServiceFeeToEvent`).
  const save = (revision, details, options = {}) => {
    if (!userId) { setState({ status: "error", message: "Your signed-in account could not be identified. Reload the page before saving." }); return }
    if (active.current) return
    const operation = { eventId, ...(competitionId ? { competitionId } : {}), action, operationId: newPlayerOperationId(), correlationId: newPlayerOperationId() }
    try { sessionStorage.setItem(key, JSON.stringify(operation)) } catch {
      setState({ status: "error", message: "Enable browser session storage before saving so the result can be recovered." })
      return
    }
    return observe(operation, () => (competitionId ? patch : post)("/admin/competition-editors/" + eventId + (competitionId ? "/" + competitionId : ""),
      { operationId: operation.operationId, revision, details, ...options, socketClientId },
      { headers: { "X-Correlation-Id": operation.correlationId } }), true)
  }
  // Like `save`, it answers the result it ends on (nothing for a request a newer one replaced).
  const recover = () => {
    const operation = active.current?.operation || state.operation
    if (operation) return observe(operation, () => post("/admin/competition-operations/" + operation.operationId + "/retry",
      { socketClientId }, { headers: { "X-Correlation-Id": operation.correlationId } }))
  }
  return { ...state, pending: ["pending", "unknown"].includes(state.status), save, recover, reset: () => { if (!active.current) setState({}) } }
}
